import {localize, translate} from "react-i18nify";
import "dayjs/locale/bg";
import "dayjs/locale/cs";
import "dayjs/locale/da";
import "dayjs/locale/de";
import "dayjs/locale/el";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/hu";
import "dayjs/locale/it";
import "dayjs/locale/is";
import "dayjs/locale/lt";
import "dayjs/locale/lv";
import "dayjs/locale/nb";
import "dayjs/locale/nl";
import "dayjs/locale/ro";
import "dayjs/locale/ru";
import "dayjs/locale/sk";
import "dayjs/locale/sr";
import "dayjs/locale/sv";
import "dayjs/locale/es";
import "dayjs/locale/pl";
import "dayjs/locale/pt";
import "dayjs/locale/zh";

import * as config from "../constants/config";
import * as helper from "../constants/helper";
import * as metaInfoUnits from "../constants/metaInfoUnits";
import * as settings from "../constants/settings";

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatDecimalByFormat = (value, format) => {
    return new Intl.NumberFormat(format, {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: window.storeProvider.getRounding()
    }).format(value);
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatDecimal = (value) => {
    if (window.storeProvider.getCustomFormatNumber() === null) {
        return localize(value, {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: window.storeProvider.getRounding()
        });
    }

    return formatDecimalByFormat(value, window.storeProvider.getCustomFormatNumber());
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatFixedDecimalByFormat = (value, rounding, format) => {
    return new Intl.NumberFormat(format, {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: rounding
    }).format(value);
};

/**
 * @param   {Number}    value
 * @param   {Number}    rounding
 * @returns {String}
 */
export const formatFixedDecimal = (value, rounding) => {
    if (window.storeProvider.getCustomFormatNumber() === null) {
        return localize(value, {style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: rounding});
    }

    return formatFixedDecimalByFormat(value, rounding, window.storeProvider.getCustomFormatNumber());
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatCurrencyByLocale = (value) => {
    return localize(value, {
        style: "currency",
        currency: window.storeProvider.getCurrencyCode(),
        currencyDisplay: "symbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: window.storeProvider.getPriceRounding()
    });
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatCurrency = (value) => {
    if (window.storeProvider.getCustomFormatCurrency() === null) {
        return formatCurrencyByLocale(value);
    }

    switch (window.storeProvider.getCustomFormatCurrency()) {
        case settings.FORMAT_CODE_PREFIX:
            return window.storeProvider.getCurrencyCode() + " " + formatCurrencyDecimal(value);
        case settings.FORMAT_CODE_POSTFIX:
            return formatCurrencyDecimal(value) + " " + window.storeProvider.getCurrencyCode();
        case settings.FORMAT_SYMBOL_PREFIX:
            return window.storeProvider.getCurrencySymbol() + " " + formatCurrencyDecimal(value);
        case settings.FORMAT_SYMBOL_POSTFIX:
            return formatCurrencyDecimal(value) + " " + window.storeProvider.getCurrencySymbol();
        default:
            return formatCurrencyDecimal(value);
    }
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatPrimaryCurrency = (value) => {
    return localize(value, {
        style: "currency",
        currency: window.storeProvider.getPrimaryCurrencyCode(),
        currencyDisplay: "symbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: window.storeProvider.getPriceRounding()
    });
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatCurrencyDecimalByFormat = (value, format) => {
    return new Intl.NumberFormat(format, {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: window.storeProvider.getPriceRounding()
    }).format(value);
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatCurrencyDecimal = (value) => {
    if (window.storeProvider.getCustomFormatNumber() === null) {
        return localize(value, {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: window.storeProvider.getPriceRounding()
        });
    }

    return formatCurrencyDecimalByFormat(value, window.storeProvider.getCustomFormatNumber());
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatPercent = (value) => {
    return localize(value, {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: window.storeProvider.getRounding()
    });
};

/**
 * @param   {Number}    value
 * @returns {String}
 */
export const formatMarginPercent = (value) => {
    return localize(value, {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
};

/**
 * Returns date and time in given format
 *
 * @param   {Object}    value
 * @param   {String}    format
 * @returns {String}
 */
export const formatDateTimeByFormat = (value, format) => {
    if (value instanceof Date) {
        return localize(value.getTime(), {dateFormat: format});
    }

    return "";
};

/**
 * Returns date and time in UTC format
 *
 * @param   {String}    value
 * @returns {String}
 */
export const formatDateUtc = (value) => {
    if (value === undefined) {
        return "";
    }

    let localTime = new Date(value);

    if (window.storeProvider.getCustomFormatDate() === null) {
        return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {dateFormat: "L"});
    }

    return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {
        dateFormat: window.storeProvider.getCustomFormatDate()
    });
};

/**
 * Returns date and time in UTC format
 *
 * @param   {String}    value
 * @returns {String}
 */
export const formatDateTimeUtc = (value) => {
    if (typeof value !== "string") {
        return "";
    }

    let localTime = new Date(value);

    if (window.storeProvider.getCustomFormatDateTime() === null) {
        return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {dateFormat: "L LTS"});
    }

    return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {
        dateFormat: window.storeProvider.getCustomFormatDateTime()
    });
};

/**
 * Returns time in UTC format
 *
 * @param   {String}    value
 * @returns {String}
 */
export const formatTimeUtc = (value) => {
    if (value === undefined) {
        return "";
    }

    let localTime = new Date(value);

    if (window.storeProvider.getCustomFormatTime() === null) {
        return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {dateFormat: "LTS"});
    }

    return localize(localTime.getTime() - localTime.getTimezoneOffset() * 60000, {
        dateFormat: window.storeProvider.getCustomFormatTime()
    });
};

/**
 * Returns date and time in locale format
 *
 * @param   {Object}    value
 * @returns {String}
 */
export const formatDate = (value) => {
    if (value instanceof Date) {
        if (window.storeProvider.getCustomFormatDate() === null) {
            return localize(value.getTime(), {dateFormat: "L"});
        }

        return localize(value.getTime(), {dateFormat: window.storeProvider.getCustomFormatDate()});
    }

    return "";
};

/**
 * Returns date and time in locale format
 *
 * @param   {Object}    value
 * @returns {String}
 */
export const formatDateTime = (value) => {
    if (value instanceof Date) {
        if (window.storeProvider.getCustomFormatDateTime() === null) {
            return localize(value.getTime(), {dateFormat: "L LTS"});
        }

        return localize(value.getTime(), {dateFormat: window.storeProvider.getCustomFormatDateTime()});
    }

    return "";
};

/**
 * Returns time in locale format
 *
 * @param   {Object}    value
 * @returns {String}
 */
export const formatTime = (value) => {
    if (value instanceof Date) {
        if (window.storeProvider.getCustomFormatTime() === null) {
            return localize(value.getTime(), {dateFormat: "LTS"});
        }

        return localize(value.getTime(), {dateFormat: window.storeProvider.getCustomFormatTime()});
    }

    return "";
};

/**
 * Returns date and time in locale format
 *
 * @param   {Date} date
 * @returns {String}
 */
export const localizeDateTime = (date) => {
    if (date instanceof Date) {
        return localize(date.getTime(), {dateFormat: "L LTS"});
    }

    return "";
};

/**
 * Returns image file name in format f.e. IMG_20191018_090611.jpg
 *
 * @returns {String}
 */
export const formatFileName = (prefix, fileType) => {
    let date = new Date();

    return (
        prefix +
        "_" +
        date.getFullYear() +
        (date.getMonth() < 9 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1) +
        (date.getDate() < 10 ? "0" + date.getDate() : date.getDate()) +
        "_" +
        (date.getHours() < 10 ? "0" + date.getHours() : date.getHours()) +
        (date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes()) +
        (date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds()) +
        "." +
        fileType
    );
};

export const formatDateTimeForServer = (date) => {
    if (date instanceof Date) {
        return (
            date.getFullYear() +
            "-" +
            (date.getMonth() < 9 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1) +
            "-" +
            (date.getDate() < 10 ? "0" + date.getDate() : date.getDate()) +
            " " +
            (date.getHours() < 10 ? "0" + date.getHours() : date.getHours()) +
            ":" +
            (date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes()) +
            ":" +
            (date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds())
        );
    }

    return null;
};

/**
 * Takes array of objects and returns array of name properties
 *
 * @param   {Array} list
 * @returns {Array}
 */
export const formatFilteringObjectsToNames = (list) => {
    return list.map((item) => {
        return item.name;
    });
};

/**
 * @param   {Number}    value
 * @returns {Number}
 */
export const formatDecimalNumber = (value) => {
    if (value === null) {
        return 0;
    }

    return Number(value.toFixed(window.storeProvider.getRounding()));
};

/**
 * @param   {Number}    value
 * @param   {Number}    decimals
 * @returns {Number}
 */
export const formatFormulaDecimalNumber = (value, decimals = config.FORMULA_DECIMALS) => {
    if (value % 1 === 0) {
        return value;
    }

    try {
        if (String(value).toLowerCase().includes("e")) {
            // Recursively increase value to avoid exponential numbers, f.e. 6.205708324991122e-8
            return formatFormulaDecimalNumber(value * 1000, decimals) / 1000;
        }

        const valueSplit = String(value).split(".");

        if (valueSplit[1].length <= decimals) {
            return value;
        }

        return Number(valueSplit[0] + "." + valueSplit[1].substr(0, decimals));
    } catch (e) {
        return 0;
    }
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertDecimalToPercent = (value) => {
    if (value === null) {
        return null;
    }

    let percent = value * 100;

    if (percent % 1 < 0.000001) {
        return Math.round(percent);
    }

    if (1 - (percent % 1) < 0.000001) {
        return Math.round(percent);
    }

    return percent;
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertPercentToDecimal = (value) => {
    if (value === null) {
        return null;
    }

    if (Math.abs(value) < 0.000001) {
        return 0;
    }

    return value / 100;
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertDecimalToMarkup = (value) => {
    if (value === null) {
        return null;
    }

    return value / (1 - value);
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertPercentToMarkup = (value) => {
    if (value === null) {
        return null;
    }

    return (value * 100) / (100 - value);
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertMarkupToDecimal = (value) => {
    if (value === null) {
        return null;
    }

    return value / (1 + value);
};

/**
 * @param       {Number}    value
 * @returns     {Number}
 */
export const convertMarkupToPercent = (value) => {
    if (value === null) {
        return null;
    }

    return (value * 100) / (100 + value);
};

export const getUnitById = (id) => {
    switch (id) {
        case 1:
            return settings.UNIT_L;
        case 2:
            return settings.UNIT_KG;
        case 3:
            return settings.UNIT_ML;
        case 4:
            return settings.UNIT_G;
        default:
            return "";
    }
};

export const getOrderSearchItemsForServer = (items) => {
    let item,
        result = [];

    for (item of items) {
        switch (item) {
            case settings.ORDER_SEARCH_BASE:
                result.push("base_name");
                break;
            case settings.ORDER_SEARCH_CUSTOMER:
                result.push("customer_name");
                break;
            case settings.ORDER_SEARCH_DATABASE:
                result.push("brand");
                break;
            case settings.ORDER_SEARCH_FANDECK:
                result.push("fandeck_name");
                result.push("fandeck_alternative_name");
                break;
            case settings.ORDER_SEARCH_FORMULA:
                result.push("color_name");
                result.push("color_alternative_name");
                break;
            case settings.ORDER_SEARCH_NOTE:
                result.push("more_info");
                break;
            case settings.ORDER_SEARCH_ORDER_ID:
                result.push("order_identifier");
                break;
            case settings.ORDER_SEARCH_PACKAGE:
                result.push("package_name");
                break;
            case settings.ORDER_SEARCH_PRODUCT:
                result.push("product_name");
                result.push("product_alternative_name");
                break;
            default:
                break;
        }
    }

    return result;
};

/**
 * @param       {String}    value
 * @returns     {String}
 */
export const deleteMultipleSpaces = (value) => {
    value = value.replace(/\s\s+/g, " ");

    return value;
};

export const getUpdateConfigurationForHelper = (configuration) => {
    let auto = {};
    let client = {};

    for (let key of Object.keys(configuration)) {
        switch (key) {
            case "autoCheck":
                auto.check = configuration.autoCheck;
                break;
            case "autoDownload":
                auto.download = configuration.autoDownload;
                break;
            case "autoForceInstall":
                auto.force_install = configuration.autoForceInstall;
                break;
            case "autoInstall":
                auto.install = configuration.autoInstall;
                break;
            case "autoInstallNewDatabases":
                auto.install_new_dbs = configuration.autoInstallNewDatabases;
                break;
            case "autoNightCheck":
                auto.night_check = configuration.autoNightCheck;
                break;
            case "autoInterval":
                auto.interval = configuration.autoInterval;
                break;
            default:
                break;
        }
    }

    return {
        auto: auto,
        client: client
    };
};

/**
 * @param       {Array | String}    List
 * @returns     {Array}
 */
export const formatList = (list) => {
    if (!Array.isArray(list)) {
        if (typeof list === "string") {
            return list;
        }

        return translate("general.notAvailable");
    }

    let resultData = "";

    let value;
    for (value of list) {
        if (resultData === "") {
            resultData = formatFixedDecimal(value, 3);
        } else {
            resultData += " " + formatFixedDecimal(value, 3);
        }
    }

    if (resultData.trim().length === 0) {
        return translate("general.notAvailable");
    }

    return resultData;
};

/**
 * @param       {Number}    value
 * @param       {String}    unit
 * @returns     {Number | String}
 */
export const formatInfoDataValue = (value, unit) => {
    if (value === null) {
        return translate("general.notAvailable");
    }

    switch (unit) {
        case metaInfoUnits.FLOAT:
            return formatFixedDecimal(value, 3);
        case metaInfoUnits.VOL_ML:
            return formatFixedDecimal(value, 3) + "\u00A0" + settings.UNIT_ML;
        case metaInfoUnits.VOL_L:
            return formatFixedDecimal(value, 3) + "\u00A0" + settings.UNIT_L;
        case metaInfoUnits.MASS_G:
            return formatFixedDecimal(value, 3) + "\u00A0" + settings.UNIT_G;
        case metaInfoUnits.MASS_KG:
            return formatFixedDecimal(value, 3) + "\u00A0" + settings.UNIT_KG;
        case metaInfoUnits.DENSITY:
            return formatFixedDecimal(value, 3) + "\u00A0" + settings.UNIT_DENSITY;
        case metaInfoUnits.LIST:
            return formatList(value);
        case metaInfoUnits.BOOLEAN:
            return value ? translate("general.yes") : translate("general.no");
        case metaInfoUnits.TABLE:
            return "";
        case metaInfoUnits.DATE:
            return formatDateUtc(value);
        case metaInfoUnits.DATETIME:
            return formatDateTimeUtc(value);
        default:
            return value;
    }
};

/**
 * Converts bytes to human friendly unit
 * @param bytes
 * @returns {Array} number and unit
 */
export const formatFileSize = (bytes) => {
    if (bytes < helper.HELPER_SIZE_KB) {
        return [bytes, bytes > 1 ? "Bytes" : "Byte"];
    }

    if (helper.HELPER_SIZE_KB <= bytes && bytes < helper.HELPER_SIZE_MB) {
        return [formatDecimal(bytes / helper.HELPER_SIZE_KB), "kB"];
    }

    if (helper.HELPER_SIZE_MB <= bytes && bytes < helper.HELPER_SIZE_GB) {
        return [formatDecimal(bytes / helper.HELPER_SIZE_MB), "MB"];
    }

    if (helper.HELPER_SIZE_GB <= bytes && bytes < helper.HELPER_SIZE_TB) {
        return [formatDecimal(bytes / helper.HELPER_SIZE_GB), "GB"];
    }

    if (helper.HELPER_SIZE_TB <= bytes) {
        return [formatDecimal(bytes / helper.HELPER_SIZE_TB), "TB"];
    }

    return ["", ""];
};

/**
 * Escapes JSON request.
 *
 * @param   {Object}    request
 * @returns {String}
 */
export const escapeJsonRequest = (request) => {
    let jsonRequest = JSON.stringify(request);

    if (jsonRequest.match(/[\u007f-\uffff]/g) === null) {
        return jsonRequest;
    }

    return jsonRequest.replace(
        /[\u007f-\uffff]/g,
        (match) => "\\u" + ("0000" + match.charCodeAt(0).toString(16)).slice(-4)
    );
};
